import React, {
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  TooltipItem,
  ChartOptions,
  Scale,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { Bubble } from "react-chartjs-2";
import { KeywordBubbleChartProps, BubbleDataPoint, TrendData } from "../types";
import { getTrendColor, getSimilarityColor } from "../config/colorScale";
import { formatCenterTrendNumber } from "../utils/trendCellDisplay";
import {
  labelAnchor,
  labelOffset,
  labelSizeForText,
  layoutLabels,
  sideToAlign,
  LabelPlacement,
  LABEL_BORDER_WIDTH,
  LABEL_FONT,
  LABEL_FONT_CSS,
  LABEL_PADDING,
} from "../utils/bubbleLabelPlacement";
import { isCenterPlottable } from "../utils/clusterCenter";
import BubbleChartLegends from "./BubbleChartLegends";

// Paints a solid white background directly onto the canvas pixels (not just
// CSS) so the browser's native "Save image as" doesn't render on transparent
// pixels, which most image viewers composite as black.
const canvasBackgroundColorPlugin = {
  id: "canvasBackgroundColor",
  beforeDraw: (chart: ChartJS) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

ChartJS.register(
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  ChartDataLabels,
  canvasBackgroundColorPlugin,
);

export interface KeywordBubbleChartRef {
  getChartInstance: () => ChartJS | null;
}

// Fraction of an axis' span left as empty margin below zero, so bubbles sitting
// at 0 are drawn just inside the plot instead of bisected by the axis line.
const ORIGIN_INSET = 0.04;

const insetAxisMin = (rawMin: number, max: number): number =>
  rawMin > 0 ? rawMin : -(max * ORIGIN_INSET);

// Chart.js puts a tick on the scale minimum, which for the inset scales above is
// a negative number no one should see. Swap that negative slice for a tick at
// zero, keeping the generated positive ticks (including the max) as they are.
// Scales that never dip below zero are left alone: a zero tick outside their
// range makes Chart.js reserve room to draw it, squashing the plot area.
const dropNegativeTicks = (scale: Scale) => {
  if (scale.min >= 0) return;
  scale.ticks = [{ value: 0 }, ...scale.ticks.filter((t) => t.value > 0)];
};

// A label's accent is the colour of the point it belongs to, so labels stay tied
// to their bubble in both trend and similarity mode. Falls back to near-black if
// a dataset somehow has no colour, which keeps the label readable either way.
const labelAccentColor = (context: { dataset: { backgroundColor?: unknown } }) =>
  typeof context.dataset.backgroundColor === "string"
    ? context.dataset.backgroundColor
    : "#000";

// Helper function to calculate bubble radius based on ranking (1-100+, larger = better ranking)
const getBubbleRadius = (ranking: number): number => {
  if (ranking === 0) {
    return 4;
  }

  const minRadius = 5;
  const maxRadius = 25;
  const clamped = Math.min(ranking, 100);
  return maxRadius - ((clamped - 1) / 99) * (maxRadius - minRadius);
};

const KeywordBubbleChart = forwardRef<KeywordBubbleChartRef, KeywordBubbleChartProps>(({
  data,
  useDynamicAxes = true,
  showLabels = false,
  colorMode = "trend",
  trendPeriod = "week",
  animationDuration = 1000,
  fixedAxisRange,
  center,
}, ref) => {
  const chartRef = useRef<ChartJS | null>(null);

  // Expose chart instance through ref
  useImperativeHandle(ref, () => ({
    getChartInstance: () => chartRef.current,
  }));
  // Prepare chart data
  const chartData = useMemo(() => {
    // Helper function to get color based on trend using new color scale
    const getNewTrendColor = (
      trendData: TrendData | undefined | null
    ): string => {
      // Handle missing, undefined, null trend values
      if (trendData === undefined || trendData === null) {
        // Neutral trend: Use neutral color from scale
        return getTrendColor(0, -1, 1);
      }

      // Get the trend value for the selected period
      const trend = trendData[trendPeriod];

      // Handle null (no snapshot) or zero (no change) trend values
      if (trend === null || trend === undefined || trend === 0) {
        return getTrendColor(0, -1, 1);
      }

      // First, find the actual range of trends in the data (excluding nullish and zero values)
      const allTrends = data
        .map((d) => d.trend?.[trendPeriod])
        .filter(
          (t) => t !== undefined && t !== null && t !== 0 && !isNaN(t)
        ) as number[];

      if (allTrends.length === 0) {
        // If no valid trends, return neutral
        return getTrendColor(0, -1, 1);
      }

      const minTrend = Math.min(...allTrends);
      const maxTrend = Math.max(...allTrends);

      return getTrendColor(trend, minTrend, maxTrend);
    };

    // Helper function to get color based on similarity using new color scale
    const getNewSimilarityColor = (
      relevance_score: number | undefined | null
    ): string => {
      // Handle missing or undefined relevance scores
      if (relevance_score === undefined || relevance_score === null) {
        return getTrendColor(0, -1, 1); // Use neutral color
      }

      // Find the range of relevance scores to normalize
      const allScores = data
        .map((d) => d.relevance_score)
        .filter((s) => s !== undefined && s !== null) as number[];

      if (allScores.length === 0) {
        return getTrendColor(0, -1, 1);
      }

      const minScore = Math.min(...allScores);
      const maxScore = Math.max(...allScores);

      return getSimilarityColor(relevance_score, minScore, maxScore);
    };

    let processedData = data;

    // Build one dataset per keyword so react-chartjs-2 can match each bubble by
    // its dataset label (via datasetIdKey="label") rather than by array index.
    // This prevents the keyword-swap animation bug where bubbles morph into
    // different keywords when the set of visible keywords changes between frames.
    const datasets = processedData.map((keyword) => {
      const trendColor = getNewTrendColor(keyword.trend);
      const similarityColor = getNewSimilarityColor(
        keyword.openai_similarity ?? keyword.relevance_score
      );
      const selectedColor =
        colorMode === "trend" ? trendColor : similarityColor;

      // Special styling for anchor keyword - red border
      const isAnchor = keyword.isAnchor;
      const borderColor = isAnchor
        ? "#D73860" // Bright red/magenta for anchor
        : selectedColor.replace(/[\d.]+\)$/g, "1)"); // Make border solid
      const borderWidth = isAnchor ? 3 : 0;

      const point: BubbleDataPoint = {
        x: keyword.difficulty,
        y: keyword.volume,
        r: getBubbleRadius(keyword.ranking),
        keyword: keyword.keyword,
        ranking: keyword.ranking,
        trend: keyword.trend,
        relevance_score: keyword.relevance_score,
        backgroundColor: selectedColor,
        borderColor: borderColor,
        borderWidth: borderWidth,
        isAnchor: isAnchor,
        openai_similarity: keyword.openai_similarity,
      };

      return {
        // label is the stable identity key used by datasetIdKey="label"
        label: keyword.keyword,
        data: [point],
        backgroundColor: selectedColor,
        borderColor: borderColor,
        borderWidth: borderWidth,
      };
    });

    // Append the volume-weighted cluster center as a diamond marker.
    // It is deliberately NOT part of `data`, so it never contributes to the
    // keyword color-scale min/max (getNewTrendColor derives that from `data`) —
    // adding the diamond can't shift the keyword bubbles' colors. In similarity
    // mode the center has no similarity value, so it stays neutral.
    if (isCenterPlottable(center)) {
      const centerColor =
        colorMode === "trend"
          ? getNewTrendColor(center.trend)
          : getTrendColor(0, -1, 1);
      const centerBorder = centerColor.replace(/[\d.]+\)$/g, "1)");
      const centerPoint: BubbleDataPoint = {
        x: center.difficulty,
        y: center.volume,
        r: getBubbleRadius(center.ranking),
        keyword: center.keyword,
        ranking: center.ranking,
        trend: center.trend ?? { week: null, month: null, life: null },
        backgroundColor: centerColor,
        borderColor: centerBorder,
        borderWidth: 1,
        isCenter: true,
      };
      datasets.push({
        label: center.keyword,
        data: [centerPoint],
        backgroundColor: centerColor,
        borderColor: centerBorder,
        borderWidth: 1,
        // "rectRot" is Chart.js's diamond point style — visually distinct from
        // the round keyword bubbles.
        pointStyle: "rectRot",
        // Dataset-level datalabels: override only the POSITION (not `display`,
        // which keeps inheriting the global boolean toggle — on-screen and the
        // export flag alike — and avoids the scriptable-display recursion). The
        // center often sits low-left, so a fixed top anchor with clip:false
        // keeps its long "Cluster Weighted Average" label from being clipped off
        // the plot area the way the smart keyword positioning would.
        datalabels: {
          anchor: "end",
          align: "top",
          offset: 8,
          clip: false,
        },
      } as any);
    }

    return { datasets };
  }, [data, colorMode, trendPeriod, center]);

  // Calculate dynamic axes ranges
  const axesConfig = useMemo(() => {
    let processedData = data;

    const volumes = processedData.map((d) => d.volume);
    const difficulties = processedData.map((d) => d.difficulty);

    // Include the center marker so the diamond is never clipped off-axis.
    if (center && center.volume != null) volumes.push(center.volume);
    if (center && center.difficulty != null) difficulties.push(center.difficulty);

    // If all volumes and difficulties are zero, use 0-10 for both axes
    const allZeroVolumes = volumes.every((v) => v === 0);
    const allZeroDifficulties = difficulties.every((d) => d === 0);

    if (allZeroVolumes && allZeroDifficulties) {
      const zeroRange = { min: insetAxisMin(0, 10), max: 10 };
      return {
        dynamic: { x: { ...zeroRange }, y: { ...zeroRange } },
        static: { x: { ...zeroRange }, y: { ...zeroRange } },
      };
    }

    const minVolume = Math.min(...volumes);
    const maxVolume = Math.max(...volumes);
    const minDifficulty = Math.min(...difficulties);
    const maxDifficulty = Math.max(...difficulties);

    // Add 10% padding
    const volumeRange = maxVolume - minVolume;
    const difficultyRange = maxDifficulty - minDifficulty;

    const volumePadding = (volumeRange ? volumeRange : 50) * 0.1;
    const difficultyPadding = (difficultyRange ? difficultyRange : 50) * 0.1;

    const dynamicXMax = Math.min(100, maxDifficulty + difficultyPadding);
    const dynamicYMax = maxVolume + volumePadding;
    const staticYMax = Math.max(...volumes) * 1.2;

    return {
      dynamic: {
        x: {
          min: insetAxisMin(minDifficulty - difficultyPadding, dynamicXMax),
          max: dynamicXMax,
        },
        y: {
          min: insetAxisMin(minVolume - volumePadding, dynamicYMax),
          max: dynamicYMax,
        },
      },
      static: {
        x: { min: insetAxisMin(0, 100), max: 100 },
        y: { min: insetAxisMin(0, staticYMax), max: staticYMax },
      },
    };
  }, [data, center]);

  // The last label layout, reused while the data, plot area and axis ranges are unchanged (datalabels asks for align, anchor and offset separately for every label).
  const labelLayoutCache = useRef<{
    data: typeof data;
    key: string;
    placements: LabelPlacement[];
  } | null>(null);

  // Places every keyword label from where the bubbles actually sit on screen (see utils/bubbleLabelPlacement).
  const getLabelPlacement = useCallback(
    (context: Context): LabelPlacement => {
      const { chartArea, scales, ctx } = context.chart;
      const fallback: LabelPlacement = { side: "top", offset: labelOffset("top", 0) };
      if (!chartArea) return fallback;

      const key = [
        chartArea.left, chartArea.top, chartArea.right, chartArea.bottom,
        scales.x.min, scales.x.max, scales.y.min, scales.y.max,
      ].join(",");
      let layout = labelLayoutCache.current;
      if (!layout || layout.data !== data || layout.key !== key) {
        // Scales rather than element positions: elements can still be mid-animation when datalabels resolves its options.
        const bubbles = data.map((d) => ({
          x: scales.x.getPixelForValue(d.difficulty),
          y: scales.y.getPixelForValue(d.volume),
          r: getBubbleRadius(d.ranking),
        }));

        ctx.save();
        ctx.font = LABEL_FONT_CSS;
        const labelSizes = data.map((d) => labelSizeForText(ctx.measureText(d.keyword).width));
        ctx.restore();

        layout = { data, key, placements: layoutLabels({ bubbles, labelSizes, area: chartArea }) };
        labelLayoutCache.current = layout;
      }

      // The center diamond is an extra dataset beyond `data` with its own label position, so it gets a harmless default.
      return layout.placements[context.datasetIndex] ?? fallback;
    },
    [data]
  );

  const options: ChartOptions<"bubble"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        // Global toggle only. The center diamond suppresses its own label via a
        // dataset-level `datalabels: { display: false }` (see the center dataset
        // above) — keeping this a plain boolean avoids a scriptable-display
        // recursion in chartjs-plugin-datalabels during export.
        display: showLabels,
        // Text and border carry the keyword's own colour, so a label reads as
        // belonging to its bubble in whichever mode (trend or similarity) is on.
        color: (context) => labelAccentColor(context),
        font: LABEL_FONT,
        // Side, anchor and offset all come from the same placement, so the label lands where the fit check measured it.
        align: (context) => sideToAlign(getLabelPlacement(context).side),
        anchor: (context) => {
          const point = context.dataset.data[context.dataIndex] as BubbleDataPoint;
          return labelAnchor(getLabelPlacement(context).side, point.r);
        },
        offset: (context) => getLabelPlacement(context).offset,
        formatter: (value, context) => {
          const point = value as BubbleDataPoint;
          return point.keyword;
        },
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        borderColor: (context) => labelAccentColor(context),
        borderWidth: LABEL_BORDER_WIDTH,
        borderRadius: 3,
        padding: LABEL_PADDING,
        // Clip labels that would go outside the chart area
        clip: true,
      },
      tooltip: {
        bodyFont: {
          family: "Google Sans Flex"
        },
        callbacks: {
          title: (context: TooltipItem<"bubble">[]) => {
            return ""; //(context[0].raw as BubbleDataPoint).keyword;
          },
          label: (context: TooltipItem<"bubble">) => {
            const point = context.raw as BubbleDataPoint;

            // Center diamond: label its aggregate values as "Weighted …" so the
            // reader knows these are computed averages, not a real keyword.
            if (point.isCenter) {
              // When every keyword is unranked the weighted ranking is the
              // "unranked = 100" sentinel; show N/A rather than a literal 100.
              const allUnranked =
                data.length > 0 && data.every((d) => !d.ranking);
              const centerLines = [
                point.keyword,
                `Volume: ${Math.round(point.y).toLocaleString()}`,
                `Weighted Difficulty: ${Math.round(point.x)}`,
                `Weighted Ranking: ${
                  allUnranked ? "N/A" : point.ranking
                }`,
              ];
              const centerTrend = point.trend?.[trendPeriod];
              if (centerTrend === null || centerTrend === undefined) {
                centerLines.push(`Trend (${trendPeriod}): N/A`);
              } else {
                centerLines.push(
                  `Trend (${trendPeriod}): ${formatCenterTrendNumber(centerTrend)}`
                );
              }
              centerLines.push("\n");
              return centerLines;
            }

            const tooltipLines = [
              `${point.keyword}${point.isAnchor ? " (Anchor)" : ""}`,
              `Volume: ${point.y.toLocaleString()}`,
              `Difficulty: ${point.x}`,
              `Ranking: ${point.ranking}`,
            ];

            if (colorMode === "trend") {
              const trendValue = point.trend?.[trendPeriod];
              if (trendValue === null || trendValue === undefined) {
                tooltipLines.push(`Trend (${trendPeriod}): N/A`);
              } else {
                tooltipLines.push(
                  `Trend (${trendPeriod}): ${
                    trendValue > 0 ? "+" : ""
                  }${trendValue}`
                );
              }
            } else {
              tooltipLines.push(
                `Similarity: ${
                  point.openai_similarity || point.relevance_score
                    ? (
                        point.openai_similarity ||
                        (point.relevance_score as number)
                      ).toFixed(3)
                    : "N/A"
                }`
              );
            }

            tooltipLines.push("\n");
            return tooltipLines;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        position: "bottom" as const,
        title: {
          display: true,
          text: "Keyword Difficulty",
          font: {
            size: 14,
            weight: 500,
            family: "Google Sans Flex"
          },
          color: "#bebbc6"
        },
        // A caller-supplied range (the animation pages, holding axes steady
        // across frames) gets the same origin inset as the ranges computed
        // here, so a zero-value bubble isn't bisected there either.
        min: fixedAxisRange
          ? insetAxisMin(fixedAxisRange.x.min, fixedAxisRange.x.max)
          : useDynamicAxes
            ? axesConfig.dynamic.x.min
            : axesConfig.static.x.min,
        max:
          fixedAxisRange?.x.max ??
          (useDynamicAxes ? axesConfig.dynamic.x.max : axesConfig.static.x.max),
        afterBuildTicks: dropNegativeTicks,
        grid: {
          color: "#f2f2f4",
          lineWidth: 1.5
        },
        ticks: {
          color: "#bdbac3",
          font: {
            family: "Google Sans Flex"
          }
        }
      },
      y: {
        title: {
          display: true,
          text: "Search Volume",
          font: {
            size: 14,
            weight: 500,
            family: "Google Sans Flex"
          },
          color: "#bebbc6"
        },
        min: fixedAxisRange
          ? insetAxisMin(fixedAxisRange.y.min, fixedAxisRange.y.max)
          : useDynamicAxes
            ? axesConfig.dynamic.y.min
            : axesConfig.static.y.min,
        max:
          fixedAxisRange?.y.max ??
          (useDynamicAxes ? axesConfig.dynamic.y.max : axesConfig.static.y.max),
        afterBuildTicks: dropNegativeTicks,
        grid: {
          color: "#f2f2f4",
          lineWidth: 1.5
        },
        ticks: {
          callback: function (value: unknown) {
            const numValue = value as number;
            return numValue.toLocaleString();
          },
          color: "#bdbac3",
          font: {
            family: "Google Sans Flex"
          }
        },
      },
    },
    animation: {
      duration: animationDuration
    }
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          height: "calc(100vh - 300px)",
          minHeight: "600px",
          maxHeight: "900px",
        }}
      >
        {/* datasetIdKey tells react-chartjs-2 to match datasets by their label
          (= keyword name) instead of array index, so each keyword's bubble
          animates to its own new position rather than swapping with neighbours. */}
        <Bubble
          ref={(chartInstance) => {
            if (chartInstance) {
              chartRef.current = chartInstance;
            }
          }}
          data={chartData}
          options={options}
          datasetIdKey="label"
        />
      </div>

      <BubbleChartLegends
        colorMode={colorMode}
        showCenterMarker={isCenterPlottable(center)}
      />
    </>
  );
});

KeywordBubbleChart.displayName = "KeywordBubbleChart";

export default KeywordBubbleChart;
